import React, { useEffect, useState } from 'react';
import { tenantService, subscriptionService } from '../services/tenantService';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const { register, handleSubmit, reset, setValue } = useForm();

  useEffect(() => {
    loadTenants();
    loadPlans();
  }, []);

  const loadTenants = async () => {
    try {
      const data = await tenantService.getAllTenants();
      setTenants(data.data || []);
    } catch (error) {
      toast.error('Failed to load clinics');
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const data = await subscriptionService.getAllPlans();
      setPlans(data);
    } catch (error) {
      console.error('Failed to load plans');
    }
  };

  const onSubmit = async (data) => {
    try {
      const response = await tenantService.createTenant(data);
      toast.success(response.message || 'Clinic created successfully!');
      setShowModal(false);
      reset();
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create clinic');
    }
  };

  const handleView = (tenant) => {
    setSelectedTenant(tenant);
    setShowViewModal(true);
  };

  const handleEdit = (tenant) => {
    setSelectedTenant(tenant);
    setValue('clinic_name', tenant.clinic_name);
    setValue('contact_person_name', tenant.contact_person_name);
    setValue('email', tenant.email);
    setValue('mobile', tenant.mobile);
    setValue('gstin', tenant.gstin || '');
    setValue('address', tenant.address);
    setValue('city', tenant.city);
    setValue('state', tenant.state);
    setValue('pincode', tenant.pincode);
    setValue('subscription_plan_id', tenant.subscription_plan_id);
    setShowEditModal(true);
  };

  const onEditSubmit = async (data) => {
    try {
      await tenantService.updateTenant(selectedTenant.id, data);
      toast.success('Clinic updated successfully!');
      setShowEditModal(false);
      reset();
      setSelectedTenant(null);
      loadTenants();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update clinic');
    }
  };

  const handleDeactivate = async (tenantId) => {
    if (window.confirm('Are you sure you want to deactivate this clinic?')) {
      try {
        await tenantService.updateTenant(tenantId, { status: 'suspended' });
        toast.success('Clinic deactivated successfully');
        loadTenants();
      } catch (error) {
        toast.error('Failed to deactivate clinic');
      }
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">🏥 Clinic Management</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold flex items-center gap-2"
        >
          <span>➕</span> Add New Clinic
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clinic Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact Person</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tenants.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  No clinics found. Create your first clinic!
                </td>
              </tr>
            ) : (
              tenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{tenant.clinic_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{tenant.contact_person_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{tenant.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{tenant.mobile}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {tenant.subscription_plan?.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs">
                    {tenant.subscription_start_date && tenant.subscription_end_date ? (
                      <div>
                        <div className="text-gray-600">Start: {new Date(tenant.subscription_start_date).toLocaleDateString('en-IN')}</div>
                        <div className="text-gray-600">End: {new Date(tenant.subscription_end_date).toLocaleDateString('en-IN')}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">Not set</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                      tenant.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {tenant.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button 
                      onClick={() => handleView(tenant)}
                      className="text-blue-600 hover:text-blue-800 mr-3 font-medium"
                    >
                      👁️ View
                    </button>
                    <button 
                      onClick={() => handleEdit(tenant)}
                      className="text-green-600 hover:text-green-800 mr-3 font-medium"
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      onClick={() => handleDeactivate(tenant.id)}
                      className="text-orange-600 hover:text-orange-800 font-medium"
                      disabled={tenant.status === 'suspended'}
                    >
                      {tenant.status === 'suspended' ? '⏸️ Deactivated' : '⏸️ Deactivate'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Clinic Modal */}
      {showViewModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">🏥 Clinic Details</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Clinic Name</label>
                  <p className="text-lg font-semibold">{selectedTenant.clinic_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p>
                    <span className={`px-3 py-1 text-sm rounded-full ${
                      selectedTenant.status === 'active' ? 'bg-green-100 text-green-800' :
                      selectedTenant.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedTenant.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Contact Person</label>
                  <p className="text-lg">{selectedTenant.contact_person_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-lg">{selectedTenant.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Mobile</label>
                  <p className="text-lg">{selectedTenant.mobile}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">GSTIN</label>
                  <p className="text-lg">{selectedTenant.gstin || 'N/A'}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Address</label>
                <p className="text-lg">{selectedTenant.address || 'N/A'}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">City</label>
                  <p className="text-lg">{selectedTenant.city || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">State</label>
                  <p className="text-lg">{selectedTenant.state || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Pincode</label>
                  <p className="text-lg">{selectedTenant.pincode || 'N/A'}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">📋 Subscription Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Plan</label>
                    <p className="text-lg">{selectedTenant.subscription_plan?.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Price</label>
                    <p className="text-lg font-semibold text-green-600">₹{selectedTenant.subscription_plan?.price}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Start Date</label>
                    <p className="text-lg">{selectedTenant.subscription_start_date}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">End Date</label>
                    <p className="text-lg">{selectedTenant.subscription_end_date}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowViewModal(false)}
                className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Clinic Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Add New Clinic</h2>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Clinic Name *</label>
                  <input
                    {...register('clinic_name', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="ABC Clinic"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Person *</label>
                  <input
                    {...register('contact_person_name', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Dr. John Doe"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    {...register('email', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="clinic@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Mobile *</label>
                  <input
                    {...register('mobile', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="9876543210"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">GSTIN</label>
                  <input
                    {...register('gstin')}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="27AABCU9603R1ZM"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input
                    {...register('city')}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Mumbai"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">State</label>
                  <input
                    {...register('state')}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Maharashtra"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Pincode</label>
                  <input
                    {...register('pincode')}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="400001"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <textarea
                  {...register('address')}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="2"
                  placeholder="Full address"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Subscription Plan *</label>
                <select
                  {...register('subscription_plan_id', { required: true })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select Plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - ₹{plan.price} ({plan.billing_cycle})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Subscription Start Date *</label>
                <input
                  type="date"
                  {...register('subscription_start_date', { required: true })}
                  className="w-full px-3 py-2 border rounded-lg"
                  defaultValue={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold"
                >
                  Create Clinic
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); reset(); }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Clinic Modal */}
      {showEditModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">✏️ Edit Clinic</h2>
            
            <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Clinic Name *</label>
                  <input
                    {...register('clinic_name', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="ABC Clinic"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Person *</label>
                  <input
                    {...register('contact_person_name', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Dr. John Doe"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    {...register('email', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="clinic@example.com"
                    disabled
                    title="Email cannot be changed"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Mobile *</label>
                  <input
                    {...register('mobile', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="9876543210"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">GSTIN</label>
                  <input
                    {...register('gstin')}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Address *</label>
                  <textarea
                    {...register('address', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="3"
                    placeholder="Full clinic address"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">City *</label>
                  <input
                    {...register('city', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Mumbai"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">State *</label>
                  <input
                    {...register('state', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Maharashtra"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Pincode *</label>
                  <input
                    {...register('pincode', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="400001"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Subscription Plan *</label>
                  <select
                    {...register('subscription_plan_id', { required: true })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select Plan</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - ₹{plan.price}/{plan.billing_cycle}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold"
                >
                  Update Clinic
                </button>
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); reset(); setSelectedTenant(null); }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tenants;
